import { CustomTooltip } from '@rentnerkev/tooltips'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { useState } from 'react'

import { FLAG_IMAGES } from '@/config/language.config.ts'
import useTranslationStore from '@/language/useTranslationStore.ts'
import { footerVariants, linkVariants } from '@/layouts/AuthLayout/Animations'
import { systemSettingsQueryOptions } from '@/lib/Queries/appQueries.ts'
import type { AppLanguage } from '@/shared/Types/config.types.ts'

export default function Footer() {
    const appVersion = import.meta.env.VITE_APP_VERSION || 'Dev'
    const { data: systemSettings } = useQuery(systemSettingsQueryOptions())
    const companyName = systemSettings?.companyName ?? 'Joomla Updater API'

    const { availableLanguages, language, setLanguage, t } =
        useTranslationStore()

    const [pendingLanguage, setPendingLanguage] = useState<AppLanguage | null>(
        null,
    )

    const isChangingLanguage = pendingLanguage !== null

    async function handleLanguageChange(nextLanguage: AppLanguage) {
        if (nextLanguage === language || isChangingLanguage) {
            return
        }

        try {
            setPendingLanguage(nextLanguage)
            await setLanguage(nextLanguage)
        } finally {
            setPendingLanguage(null)
        }
    }

    return (
        <motion.footer
            className="border-border-dark/50 bg-background-dark/30 absolute bottom-0 left-0 z-20 w-full border-t backdrop-blur-md"
            variants={footerVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.div
                variants={linkVariants}
                className="fixed bottom-3 left-3 z-30 flex items-center gap-2"
            >
                {availableLanguages.map((availableLanguage) => {
                    const isSelected = language === availableLanguage
                    const flagImage = FLAG_IMAGES[availableLanguage]

                    return (
                        <CustomTooltip
                            key={availableLanguage}
                            content={t(`language.${availableLanguage}`)}
                        >
                            <button
                                type="button"
                                aria-label={t(`language.${availableLanguage}`)}
                                aria-pressed={isSelected}
                                disabled={isChangingLanguage}
                                onClick={() =>
                                    void handleLanguageChange(availableLanguage)
                                }
                                className={[
                                    'rounded-full transition-transform duration-200 ease-out hover:scale-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60',
                                    isSelected
                                        ? 'ring-2 ring-primary ring-offset-2 ring-offset-background-dark'
                                        : 'opacity-70 hover:opacity-100',
                                ].join(' ')}
                            >
                                <img
                                    src={flagImage}
                                    alt={t(`language.${availableLanguage}`)}
                                    className="h-6 w-6 cursor-pointer rounded-full object-cover"
                                />
                            </button>
                        </CustomTooltip>
                    )
                })}
            </motion.div>

            <div className="mx-auto flex max-w-7xl items-center justify-center gap-6 px-6 py-4 text-[11px] font-bold tracking-widest text-gray-500 uppercase">
                <motion.div variants={linkVariants}>
                    <Link
                        to="/terms"
                        aria-label={t('layout.footer.terms')}
                        className="hover:text-primary transition-all duration-300 hover:tracking-[0.15em]"
                    >
                        {t('layout.footer.terms')}
                    </Link>
                </motion.div>

                <motion.span
                    variants={linkVariants}
                    className="bg-border-dark h-1 w-1 rounded-full"
                />

                <motion.div variants={linkVariants}>
                    <Link
                        to="/login"
                        aria-label={companyName}
                        className="hover:text-primary flex items-center transition-all duration-300 hover:tracking-[0.15em]"
                    >
                        &copy; {new Date().getFullYear()} {companyName}
                    </Link>
                </motion.div>

                <motion.span
                    variants={linkVariants}
                    className="bg-border-dark h-1 w-1 rounded-full"
                />

                <motion.div variants={linkVariants}>
                    <Link
                        to="/privacy"
                        aria-label={t('layout.footer.privacy')}
                        className="hover:text-primary transition-all duration-300 hover:tracking-[0.15em]"
                    >
                        {t('layout.footer.privacy')}
                    </Link>
                </motion.div>
            </div>

            <motion.div
                variants={linkVariants}
                className="fixed right-3 bottom-3"
            >
                <div className="border-border-dark/50 bg-border-dark/30 rounded border px-2 py-1 font-mono text-[10px] tracking-tighter text-gray-500">
                    {appVersion}
                </div>
            </motion.div>
        </motion.footer>
    )
}
